/**
 * Reports Hub Page (Admin)
 *
 * Dashboard with links to all report types organized by category.
 */

import { Link } from 'react-router-dom';

export default function ReportsPage() {
  return (
    <div>
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="text-gray-600 mt-1">
          Generate reports and export data for payroll, costs, and crew analytics
        </p>
      </div>

      {/* Report categories */}
      <div className="space-y-8">
        {/* Financial Reports */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <DollarIcon className="w-5 h-5 mr-2 text-green-600" />
            Financial Reports
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ReportCard
              to="/admin/reports/payroll"
              title="Payroll Report"
              description="Crew payment totals by member"
              icon={<UsersIcon className="w-6 h-6" />}
            />
            <ReportCard
              to="/admin/reports/event-costs"
              title="Event Costs"
              description="Cost breakdown per event"
              icon={<CalendarIcon className="w-6 h-6" />}
            />
            <ReportCard
              to="/admin/reports/costs-by-sport"
              title="Costs by Sport"
              description="Costs grouped by sport"
              icon={<ChartIcon className="w-6 h-6" />}
            />
            <ReportCard
              to="/admin/reports/costs-by-event-type"
              title="Costs by Event Type"
              description="Costs grouped by event type"
              icon={<ChartIcon className="w-6 h-6" />}
            />
          </div>
        </section>

        {/* Payroll Exports */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <DownloadIcon className="w-5 h-5 mr-2 text-blue-600" />
            Payroll Exports
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ReportCard
              to="/admin/reports/payroll-export"
              title="Payroll Summary Export"
              description="One row per crew member"
              icon={<DocumentIcon className="w-6 h-6" />}
              badge="CSV"
            />
            <ReportCard
              to="/admin/reports/timesheet-export"
              title="Timesheet Export"
              description="Detailed timesheet with all assignments"
              icon={<ClockIcon className="w-6 h-6" />}
              badge="CSV"
            />
            <ReportCard
              to="/admin/reports/crew-by-event"
              title="Crew by Event"
              description="All crew and hours entered for each event"
              icon={<CalendarIcon className="w-6 h-6" />}
              badge="CSV"
            />
          </div>
        </section>

        {/* Crew Reports */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <UsersIcon className="w-5 h-5 mr-2 text-purple-600" />
            Crew Reports
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ReportCard
              to="/admin/reports/crew-utilization"
              title="Crew Utilization"
              description="Assignment and confirmation rates"
              icon={<UsersIcon className="w-6 h-6" />}
            />
            <ReportCard
              to="/admin/reports/availability-analytics"
              title="Availability Analytics"
              description="Response patterns and availability rates"
              icon={<ChartIcon className="w-6 h-6" />}
            />
            <ReportCard
              to="/admin/reports/assignment-response-rates"
              title="Response Rates"
              description="Confirm/decline trends over time"
              icon={<TrendIcon className="w-6 h-6" />}
            />
          </div>
        </section>

        {/* Operations Reports */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <ClipboardIcon className="w-5 h-5 mr-2 text-orange-600" />
            Operations Reports
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ReportCard
              to="/admin/reports/staffing-status"
              title="Staffing Status"
              description="Event staffing gaps and fill rates"
              icon={<ClipboardIcon className="w-6 h-6" />}
            />
          </div>
        </section>

        {/* Quick Links */}
        <section className="mt-8 pt-8 border-t border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Links</h2>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/admin/pay-periods"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <CalendarIcon className="w-5 h-5 mr-2 text-gray-400" />
              Pay Periods
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

interface ReportCardProps {
  to: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  badge?: string;
}

function ReportCard({ to, title, description, icon, badge }: ReportCardProps) {
  return (
    <Link
      to={to}
      className="block bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:border-primary-300 hover:shadow transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="text-gray-400">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-gray-900">{title}</h3>
            {badge && (
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                {badge}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
        <ChevronRightIcon className="w-5 h-5 text-gray-400" />
      </div>
    </Link>
  );
}

// =============================================================================
// Icons
// =============================================================================

function DollarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function ChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function TrendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  );
}

function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}
